import { useCallback, useEffect, useState } from 'react';
import { api, errorMessage, type Job } from '../api';
import { formatDateTime, relativeTime } from '../format';
import { useInterval } from '../hooks';
import { Alert } from './Alert';
import { JOB_TYPE_LABELS, JobDetail } from './JobDetail';
import { StatusBadge } from './StatusBadge';

export function JobsTab({ focusJobId }: { focusJobId?: string | null }) {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(focusJobId ?? null);

  const load = useCallback(async () => {
    try {
      setJobs(await api.jobs(50));
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const active = (jobs ?? []).some((j) => j.status === 'queued' || j.status === 'running');
  useInterval(() => void load(), active ? 3000 : null);

  const current = jobs?.find((j) => j.id === selected) ?? null;

  return (
    <div className="stack">
      {error && <Alert>{error}</Alert>}
      {jobs === null && !error && (
        <p className="muted" aria-busy="true">
          Chargement des tâches…
        </p>
      )}
      {jobs?.length === 0 && <p className="muted">Aucune tâche pour le moment.</p>}
      {jobs && jobs.length > 0 && (
        <div className="split">
          <div className="table-wrap">
            <table>
              <caption className="sr-only">Tâches récentes</caption>
              <thead>
                <tr>
                  <th scope="col">Type</th>
                  <th scope="col">Utilisateur</th>
                  <th scope="col">Statut</th>
                  <th scope="col">Créée</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className={j.id === selected ? 'row-selected' : undefined}>
                    <td>
                      {JOB_TYPE_LABELS[j.type]}
                      {j.dryRun && <span className="muted"> (simulation)</span>}
                    </td>
                    <td>{j.username}</td>
                    <td>
                      <StatusBadge status={j.status} />
                    </td>
                    <td title={formatDateTime(j.createdAt)}>{relativeTime(j.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-small"
                        aria-pressed={j.id === selected}
                        onClick={() => setSelected(j.id === selected ? null : j.id)}
                      >
                        Détails<span className="sr-only"> de la tâche {j.id}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <aside className="panel" aria-label="Détail de la tâche" aria-live="polite">
            {current ? (
              <JobDetail job={current} />
            ) : (
              <p className="muted">Sélectionnez une tâche pour voir ses étapes.</p>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
